// src/components/kiosk/POSSection/ItemsGrid.jsx
// OPTIMIZED: Smaller cards with readable text
import { formatCurrency } from '../../../utils/formatters';
import { Package, AlertCircle } from 'lucide-react';

function ItemsGrid({ items, onAddToCart, search, onSearchChange, onBack, selectedSection, selectedCategory }) {
  // Section color mapping
  const sectionColors = {
    'Salon': { 
      bg: '#10b981', 
      bgLight: 'rgba(16, 185, 129, 0.15)', 
      bgCard: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
      border: '#10b981', 
      text: '#ffffff',
      textDark: '#065f46',
      shadow: 'rgba(16, 185, 129, 0.3)'
    },
    'Spa': { 
      bg: '#a855f7', 
      bgLight: 'rgba(168, 85, 247, 0.15)', 
      bgCard: 'linear-gradient(135deg, #a855f7 0%, #7c3aed 100%)',
      border: '#a855f7', 
      text: '#ffffff',
      textDark: '#6b21a8',
      shadow: 'rgba(168, 85, 247, 0.3)'
    },
    'Gym': { 
      bg: '#3b82f6', 
      bgLight: 'rgba(59, 130, 246, 0.15)', 
      bgCard: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
      border: '#3b82f6', 
      text: '#ffffff',
      textDark: '#1e40af',
      shadow: 'rgba(59, 130, 246, 0.3)'
    }
  };

  const colors = sectionColors[selectedSection] || sectionColors['Gym'];

  if (items.length === 0) {
    return (
      <div style={{ fontFamily: 'Inter, system-ui, sans-serif' }}>
        <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
          <h2
            className="text-xl md:text-4xl"
            style={{
              fontWeight: '700',
              color: '#ffffff',
              marginBottom: '0.5rem',
              textShadow: '0 2px 4px rgba(0,0,0,0.3)'
            }}
          >
            {selectedSection} {selectedCategory ? `- ${selectedCategory.name}` : ''}
          </h2>
          <div 
            style={{
              width: '8rem',
              height: '4px',
              margin: '0 auto',
              borderRadius: '9999px',
              background: colors.bg
            }}
          ></div>
        </div>

        <div style={{ marginBottom: '1.5rem' }}>
          <div style={{ marginBottom: '1rem' }}>
            <input
              type="text"
              placeholder="Search items by name..."
              value={search}
              onChange={(e) => onSearchChange(e.target.value)}
              style={{
                width: '100%',
                padding: '1rem',
                fontSize: '14px',
                border: `2px solid ${colors.border}`,
                borderRadius: '0.75rem',
                fontFamily: 'Inter, sans-serif',
                backgroundColor: 'rgba(255, 255, 255, 0.95)',
                color: '#1f2937',
                boxSizing: 'border-box'
              }}
            />
          </div>

          <button
            onClick={onBack}
            style={{
              padding: '0.5rem 1.5rem',
              fontSize: '13px',
              fontWeight: '600',
              border: '1px solid rgba(255, 255, 255, 0.3)',
              borderRadius: '0.5rem',
              background: 'rgba(255, 255, 255, 0.1)',
              color: '#ffffff',
              cursor: 'pointer',
              fontFamily: 'Inter, sans-serif',
              backdropFilter: 'blur(10px)'
            }}
          >
            ← Back to Categories
          </button>
        </div>

        <div style={{ textAlign: 'center', padding: '4rem' }}>
          <Package size={64} style={{ color: 'rgba(255, 255, 255, 0.3)', margin: '0 auto 1rem' }} />
          <p style={{ fontSize: '1.125rem', fontWeight: '600', color: '#ffffff', margin: 0 }}>No items found</p>
          <p style={{ fontSize: '0.875rem', color: 'rgba(255, 255, 255, 0.7)', marginTop: '0.5rem' }}>Try adjusting your search</p>
        </div>
      </div>
    );
  }

  return (
    <div style={{ fontFamily: 'Inter, system-ui, sans-serif' }}>
      {/* Title */}
      <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
        <h2
          className="text-xl md:text-4xl"
          style={{
            fontWeight: '700',
            color: '#ffffff',
            marginBottom: '0.5rem',
            fontFamily: 'Inter, sans-serif',
            textShadow: '0 2px 4px rgba(0,0,0,0.3)'
          }}
        >
          {selectedSection} {selectedCategory ? `- ${selectedCategory.name}` : ''}
        </h2>
        <div 
          style={{
            width: '8rem',
            height: '4px',
            margin: '0 auto',
            borderRadius: '9999px',
            background: colors.bg
          }}
        ></div>
      </div>

      {/* Search Bar */}
      <div style={{ marginBottom: '1.5rem' }}>
        <div style={{ marginBottom: '1rem' }}>
          <input
            type="text"
            placeholder="Search items by name..."
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
            style={{
              width: '100%',
              padding: '1rem',
              fontSize: '14px',
              border: `2px solid ${colors.border}`,
              borderRadius: '0.75rem',
              fontFamily: 'Inter, sans-serif',
              backgroundColor: 'rgba(255, 255, 255, 0.95)',
              color: '#1f2937',
              backdropFilter: 'blur(10px)',
              boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
              boxSizing: 'border-box'
            }}
          />
        </div>

        {/* Back Button */}
        <button
          onClick={onBack}
          style={{
            padding: '0.5rem 1.5rem',
            fontSize: '13px',
            fontWeight: '600',
            border: '1px solid rgba(255, 255, 255, 0.3)',
            borderRadius: '0.5rem',
            background: 'rgba(255, 255, 255, 0.1)',
            color: '#ffffff',
            cursor: 'pointer',
            fontFamily: 'Inter, sans-serif',
            backdropFilter: 'blur(10px)',
            transition: 'all 0.3s ease'
          }}
          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.2)'}
          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.1)'}
        >
          ← Back to Categories
        </button>
      </div>

      {/* Items Grid - SMALLER CARDS */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))',
          gap: '0.75rem',
          paddingBottom: '2rem'
        }}
      >
        {items.map(item => (
          <ItemCard key={item._id} item={item} onAddToCart={onAddToCart} sectionColors={colors} />
        ))}
      </div>
    </div>
  );
}

function ItemCard({ item, onAddToCart, sectionColors }) {
  const isLowStock = item.type === 'product' && item.quantity < 10;
  const isOutOfStock = item.type === 'product' && item.quantity === 0;

  return (
    <button
      onClick={() => !isOutOfStock && onAddToCart(item)}
      disabled={isOutOfStock}
      style={{
        padding: '0',
        border: `2px solid ${sectionColors.border}`,
        borderRadius: '0.75rem',
        background: '#ffffff',
        textAlign: 'left',
        cursor: isOutOfStock ? 'not-allowed' : 'pointer',
        opacity: isOutOfStock ? 0.6 : 1,
        transition: 'all 0.3s ease',
        fontFamily: 'Inter, system-ui, sans-serif',
        boxShadow: `0 2px 4px ${sectionColors.shadow}`,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        height: '100%'
      }}
      onMouseEnter={(e) => {
        if (!isOutOfStock) {
          e.currentTarget.style.transform = 'translateY(-4px) scale(1.02)';
          e.currentTarget.style.boxShadow = `0 8px 16px ${sectionColors.shadow}`;
        }
      }}
      onMouseLeave={(e) => {
        if (!isOutOfStock) {
          e.currentTarget.style.transform = 'translateY(0) scale(1)';
          e.currentTarget.style.boxShadow = `0 2px 4px ${sectionColors.shadow}`;
        }
      }}
    >
      {/* Colored Header - COMPACT */}
      <div 
        style={{
          padding: '0.75rem',
          background: sectionColors.bgCard,
          color: sectionColors.text,
          position: 'relative',
          overflow: 'hidden',
          minHeight: '80px',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center'
        }}
      >
        <div
          style={{
            position: 'absolute',
            width: '80px',
            height: '80px',
            borderRadius: '50%',
            background: 'rgba(255, 255, 255, 0.1)',
            top: '-20px',
            right: '-20px',
            filter: 'blur(15px)'
          }}
        />
        
        <div style={{ position: 'relative', zIndex: 1 }}>
          {/* Item Name - SMALLER */}
          <h3
            style={{
              fontSize: '0.8rem',
              fontWeight: '700',
              color: '#ffffff',
              margin: '0 0 0.4rem 0',
              lineHeight: '1.2',
              minHeight: '2rem',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden'
            }}
          >
            {item.name}
          </h3>

          {/* Type Badge - SMALLER */}
          <span
            style={{
              display: 'inline-block',
              padding: '0.15rem 0.5rem',
              fontSize: '0.65rem',
              fontWeight: '600',
              borderRadius: '9999px',
              background: 'rgba(255, 255, 255, 0.25)',
              color: '#ffffff'
            }}
          >
            {item.type === 'product' ? 'Product' : 'Service'}
          </span>

          {isLowStock && !isOutOfStock && (
            <AlertCircle size={14} style={{ color: '#fbbf24', position: 'absolute', top: '0.75rem', right: '0.75rem' }} />
          )}
        </div>
      </div>

      {/* White Content Area - COMPACT */}
      <div style={{ padding: '0.75rem', flex: 1, display: 'flex', flexDirection: 'column' }}>
        {/* Details - SMALLER TEXT */}
        <div style={{ fontSize: '0.65rem', color: '#6b7280', marginBottom: '0.5rem', flex: 1 }}>
          {item.category && (
            <p style={{ margin: '0 0 0.25rem 0', fontWeight: '500' }}>
              📁 {typeof item.category === 'string' ? item.category : item.category.name}
            </p>
          )}
          {item.type === 'product' && (
            <p style={{ margin: 0, color: isLowStock ? '#f59e0b' : '#6b7280', fontWeight: isLowStock ? '600' : '400' }}>
              📦 {item.quantity} {item.unit || 'units'}
            </p>
          )}
        </div>

        {/* Price - COMPACT */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            paddingTop: '0.5rem',
            borderTop: `1px solid ${sectionColors.bgLight}`,
            marginTop: 'auto'
          }}
        >
          <div style={{ flex: 1 }}>
            <p style={{ fontSize: '0.6rem', color: '#6b7280', margin: '0 0 0.15rem 0' }}>Price</p>
            <span style={{ fontSize: '1rem', fontWeight: '700', color: sectionColors.textDark, lineHeight: 1 }}>
              {formatCurrency(item.price)}
            </span>
          </div>
          {isOutOfStock && (
            <span style={{ fontSize: '0.65rem', fontWeight: '600', color: '#dc2626' }}>
              Out
            </span>
          )}
        </div>
      </div>
    </button>
  );
}

export default ItemsGrid;